import {
  clearTaskStepAssignee as clearTaskStepAssigneeRequest,
  setTaskStepAssignee as setTaskStepAssigneeRequest,
} from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import { useOptimisticTaskPatch } from './useOptimisticTaskPatch';
import type { UserSummary } from '@/lib/api/generated/model';

export interface SetStepAssigneeInput {
  workspaceId: string;
  taskId: string;
  /** The gate being redirected — in the body, because step ids are free-form. */
  stepId: string;
  assigneeUserId: string;
  /**
   * The chosen person, for the optimistic patch. The picker already holds them,
   * and looking them up again here would be a second read of the directory to
   * learn what the caller just picked.
   */
  person: UserSummary | null;
}

/** Route one gate on one task to somebody, leaving the workflow alone (FR-030). */
export function useSetStepAssignee() {
  return useOptimisticTaskPatch<SetStepAssigneeInput>({
    send: async ({ workspaceId, taskId, stepId, assigneeUserId }) =>
      expectStatus(
        await setTaskStepAssigneeRequest(workspaceId, taskId, {
          step_id: stepId,
          assignee_user_id: assigneeUserId,
        }),
        200,
        'The assignee could not be saved.',
      ),
    optimistic: ({ person }) => ({
      assignee: person,
      assignee_is_override: true,
    }),
    settled: (task) => ({
      assignee: task.assignee,
      assignee_is_override: task.assignee_is_override,
    }),
  });
}

export interface ClearStepAssigneeInput {
  workspaceId: string;
  taskId: string;
  stepId: string;
  /**
   * Whom the workflow itself names at this gate, so the optimistic patch can
   * show where the task is about to land rather than blanking the row and then
   * filling it back in. `null` when the definition names nobody either.
   */
  definitionAssignee: UserSummary | null;
}

/** Return one gate on this task to the workflow's own assignee (FR-032). */
export function useClearStepAssignee() {
  return useOptimisticTaskPatch<ClearStepAssigneeInput>({
    send: async ({ workspaceId, taskId, stepId }) =>
      expectStatus(
        await clearTaskStepAssigneeRequest(workspaceId, taskId, {
          step_id: stepId,
        }),
        200,
        'The assignee could not be cleared.',
      ),
    optimistic: ({ definitionAssignee }) => ({
      assignee: definitionAssignee,
      assignee_is_override: false,
    }),
    // The server resolves override -> workflow -> nobody, and a cleared gate lands
    // on whoever the workflow names — which the optimistic guess above can only
    // approximate from what the board happened to be showing.
    settled: (task) => ({
      assignee: task.assignee,
      assignee_is_override: task.assignee_is_override,
    }),
  });
}
